import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEquipment, deleteEquipment, getCategories } from '../../services/supabase';
import { Button } from '../../components/ui/Button';
import { supabaseServiceRole } from '../../services/supabase';
import { Link } from 'react-router-dom';

const AdminEquipmentList = () => {
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const queryClient = useQueryClient();

    // Получаем список оборудования
    const {
        data: equipmentData,
        isLoading: equipmentLoading,
        error: equipmentError
    } = useQuery({
        queryKey: ['equipment', selectedCategory],
        queryFn: async () => {
            const { data, error } = await getEquipment(selectedCategory || undefined);
            if (error) throw error;
            return data;
        },
    });

    // Получаем список категорий
    const {
        data: categoriesData,
        isLoading: categoriesLoading
    } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const { data, error } = await getCategories();
            if (error) throw error;
            return data;
        },
    });

    // Мутация для удаления оборудования
    const deleteEquipmentMutation = useMutation({
        mutationFn: (id: string) => deleteEquipment(id),
        onSuccess: () => {
            // Инвалидируем запрос equipment для обновления списка
            queryClient.invalidateQueries({ queryKey: ['equipment'] });
        },
    });

    // Мутация для изменения порядка оборудования (перемещение вверх или вниз)
    const moveOrderMutation = useMutation({
        mutationFn: async ({ equipmentId, direction }: { equipmentId: string, direction: 'up' | 'down' }) => {
            try {
                if (!equipmentData || equipmentData.length === 0) {
                    throw new Error('Список оборудования пуст');
                }

                const equipmentToMove = equipmentData.find(eq => eq.id === equipmentId);
                if (!equipmentToMove) {
                    throw new Error('Оборудование не найдено');
                }

                // Находим текущий индекс оборудования в массиве
                const currentIndex = equipmentData.findIndex(eq => eq.id === equipmentId);
                if (currentIndex === -1) {
                    throw new Error('Оборудование не найдено в списке');
                }

                // Фильтруем оборудование по той же категории, если оно фильтруется
                const sameCategory = equipmentData.filter(eq =>
                    (!selectedCategory || eq.category_id === equipmentToMove.category_id) &&
                    eq.category_id === equipmentToMove.category_id
                );

                // Находим индекс в фильтрованном списке
                const currentIndexInCategory = sameCategory.findIndex(eq => eq.id === equipmentId);

                // Определяем новый индекс в зависимости от направления
                let newIndex;
                if (direction === 'up') {
                    // Если оборудование уже первое в своей категории, ничего не делаем
                    if (currentIndexInCategory === 0) {
                        console.log('Нет оборудования выше в этой категории');
                        return null;
                    }
                    newIndex = currentIndexInCategory - 1;
                } else {
                    // Если оборудование уже последнее в своей категории, ничего не делаем
                    if (currentIndexInCategory === sameCategory.length - 1) {
                        console.log('Нет оборудования ниже в этой категории');
                        return null;
                    }
                    newIndex = currentIndexInCategory + 1;
                }

                // Получаем оборудование, с которым меняемся местами
                const swapWithEquipment = sameCategory[newIndex];

                // Получаем текущие значения sort_order обоих элементов
                // Если sort_order не задан, используем позицию в списке
                const equipmentToMoveSortOrder = equipmentToMove.sort_order !== null && equipmentToMove.sort_order !== undefined
                    ? equipmentToMove.sort_order
                    : currentIndexInCategory + 1;

                const swapWithEquipmentSortOrder = swapWithEquipment.sort_order !== null && swapWithEquipment.sort_order !== undefined
                    ? swapWithEquipment.sort_order
                    : newIndex + 1;

                console.log(`Меняем местами: ${equipmentToMove.name} (${equipmentToMoveSortOrder}) <-> ${swapWithEquipment.name} (${swapWithEquipmentSortOrder})`);

                // Обновляем порядок обоих элементов
                const updatePromises = [
                    supabaseServiceRole
                        .from('equipment')
                        .update({ sort_order: swapWithEquipmentSortOrder })
                        .eq('id', equipmentToMove.id),
                    supabaseServiceRole
                        .from('equipment')
                        .update({ sort_order: equipmentToMoveSortOrder })
                        .eq('id', swapWithEquipment.id)
                ];

                const results = await Promise.all(updatePromises);

                // Проверяем ошибки в каждом из запросов
                for (const result of results) {
                    if (result.error) throw result.error;
                }

                return { success: true };
            } catch (error) {
                console.error('Ошибка при изменении порядка оборудования:', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['equipment'] });
        },
        onError: (error) => {
            console.error('Ошибка при изменении порядка оборудования:', error);
            alert('Не удалось изменить порядок оборудования.');
        }
    });

    const handleDelete = async (id: string) => {
        if (window.confirm('Вы уверены, что хотите удалить это оборудование?')) {
            await deleteEquipmentMutation.mutateAsync(id);
        }
    };

    // Обработчик перемещения оборудования вверх
    const handleMoveUp = (id: string) => {
        moveOrderMutation.mutate({ equipmentId: id, direction: 'up' });
    };

    // Обработчик перемещения оборудования вниз
    const handleMoveDown = (id: string) => {
        moveOrderMutation.mutate({ equipmentId: id, direction: 'down' });
    };

    const isLoading = equipmentLoading || categoriesLoading;

    // Счетчик доступного оборудования
    const availableCount = equipmentData?.filter(item => item.status === 'available').length || 0;

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            {/* Хлебные крошки */}
            <div className="flex items-center text-sm text-gray-500 mb-4">
                <Link to="/admin" className="hover:text-primary">
                    Панель администратора
                </Link>
                <span className="mx-2">→</span>
                <span className="text-gray-700 font-medium">Управление оборудованием</span>
            </div>

            {/* Заголовок и верхняя панель */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Управление оборудованием</h1>
                    {!isLoading && equipmentData && (
                        <p className="text-gray-500 mt-1">
                            Всего: {equipmentData.length} позиций | Единиц: {equipmentData.reduce((sum, item) => sum + (item.quantity || 1), 0)} | Доступно: {availableCount}
                        </p>
                    )}
                </div>
                <Button
                    to="/admin/equipment/new"
                    variant="primary"
                    className="md:self-start"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Добавить оборудование
                </Button>
            </div>

            {/* Фильтр по категориям */}
            {!categoriesLoading && categoriesData && (
                <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center flex-wrap gap-2">
                        <span className="text-gray-700 font-medium mr-2">Фильтр по категориям:</span>
                        <Button
                            variant={!selectedCategory ? 'primary' : 'secondary'}
                            size="sm"
                            onClick={() => setSelectedCategory(null)}
                        >
                            Все категории
                        </Button>
                        {categoriesData.map((category: any) => (
                            <Button
                                key={category.id}
                                variant={selectedCategory === category.id ? 'primary' : 'secondary'}
                                size="sm"
                                onClick={() => setSelectedCategory(category.id)}
                            >
                                {category.name}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {/* Таблица оборудования */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin mb-4"></div>
                    <p className="text-gray-500">Загрузка данных...</p>
                </div>
            ) : equipmentError ? (
                <div className="bg-red-50 text-red-600 p-6 rounded-lg border border-red-200 mb-6">
                    <div className="flex items-center mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Ошибка при загрузке данных</span>
                    </div>
                    <p>{(equipmentError as Error).message}</p>
                </div>
            ) : equipmentData && equipmentData.length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
                    <div className="overflow-x-auto">
                        <table className="w-full table-auto">
                            <thead className="bg-gray-50 text-gray-700">
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium text-sm">Название</th>
                                    <th className="px-4 py-3 text-left font-medium text-sm">Категория</th>
                                    <th className="px-4 py-3 text-left font-medium text-sm">Количество</th>
                                    <th className="px-4 py-3 text-left font-medium text-sm">Цена (₽/день)</th>
                                    <th className="px-4 py-3 text-center font-medium text-sm">Порядок</th>
                                    <th className="px-4 py-3 text-center font-medium text-sm">Действия</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {equipmentData.map((item, index) => {
                                    const category = categoriesData?.find(
                                        (cat: any) => cat.id === item.category_id
                                    );

                                    // Считаем позицию в категории для корректного отображения стрелок
                                    const sameCategory = equipmentData.filter(eq =>
                                        !selectedCategory || eq.category_id === item.category_id
                                    );
                                    const categoryIndex = sameCategory.findIndex(eq => eq.id === item.id);
                                    const isFirst = categoryIndex === 0;
                                    const isLast = categoryIndex === sameCategory.length - 1;

                                    return (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center">
                                                    {item.image_url ? (
                                                        <img
                                                            src={item.image_url}
                                                            alt={item.name}
                                                            className="w-10 h-10 object-cover rounded-md mr-3"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 bg-gray-100 rounded-md mr-3 flex items-center justify-center text-gray-400">
                                                            Нет
                                                        </div>
                                                    )}
                                                    <span className="font-medium text-gray-800">{item.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">{category?.name || 'Нет категории'}</td>
                                            <td className="px-4 py-3 text-gray-700 font-medium">
                                                {item.quantity || 1}
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 font-medium">
                                                {item.daily_rate ? `${item.daily_rate} ₽` : '-'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center">
                                                    <div className="flex items-center border border-gray-200 rounded-md">
                                                        <button
                                                            className={`p-1 px-2 border-r border-gray-200 ${isFirst ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                                                            onClick={() => !isFirst && handleMoveUp(item.id)}
                                                            disabled={isFirst}
                                                            title="Переместить вверх"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                            </svg>
                                                        </button>
                                                        <span className="px-3 py-1 text-gray-700 text-sm font-medium min-w-[2rem] text-center">
                                                            {item.sort_order || index + 1}
                                                        </span>
                                                        <button
                                                            className={`p-1 px-2 border-l border-gray-200 ${isLast ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}
                                                            onClick={() => !isLast && handleMoveDown(item.id)}
                                                            disabled={isLast}
                                                            title="Переместить вниз"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex justify-center space-x-2">
                                                    <Button
                                                        to={`/admin/equipment/edit/${item.id}`}
                                                        variant="secondary"
                                                        size="sm"
                                                    >
                                                        Изменить
                                                    </Button>
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() => handleDelete(item.id)}
                                                        disabled={deleteEquipmentMutation.isPending}
                                                    >
                                                        {deleteEquipmentMutation.isPending &&
                                                            deleteEquipmentMutation.variables === item.id
                                                            ? 'Удаление...'
                                                            : 'Удалить'}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="bg-gray-50 rounded-lg p-12 text-center border border-gray-200 shadow-sm mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="text-xl font-medium text-gray-800 mb-3">
                        Оборудование не найдено
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                        В базе данных нет оборудования или по выбранным параметрам фильтрации ничего не найдено
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <Button variant="primary" onClick={() => setSelectedCategory(null)}>
                            Показать все
                        </Button>
                        <Button to="/admin/equipment/new" variant="secondary">
                            Добавить оборудование
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center">
                <Button to="/admin" variant="link" className="text-gray-500 hover:text-gray-700">
                    ← Вернуться к панели администратора
                </Button>
                {!isLoading && equipmentData && equipmentData.length > 5 && (
                    <Button
                        to="/admin/equipment/new"
                        variant="outline"
                        size="sm"
                    >
                        Добавить оборудование
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AdminEquipmentList; 